import { useState, FormEvent, useRef } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ScrollToTopButton from "../../components/feature/ScrollToTopButton";
import { supabase } from "../../lib/supabase";
import SEO from "@/components/SEO";

const FORM_URL = "https://readdy.ai/api/form/d89pi55f07jn52r5r42g";

const tiposConsulta = [
  "Lector / Seguidor",
  "Medio de comunicación",
  "Propuesta de colaboración",
  "Publicidad / Patrocinio",
  "Otra consulta",
];

export default function Contacto() {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [tipo, setTipo] = useState("");
  const [asunto, setAsunto] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!nombre || !email || !tipo || !asunto || !mensaje) {
      setError("Por favor completa todos los campos antes de enviar.");
      return;
    }
    if (mensaje.length > 500) {
      setError("El mensaje no puede superar los 500 caracteres.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // Guardar también en Supabase para que Carmen los vea en el admin
      await supabase.from("contact_messages").insert([
        {
          name: nombre,
          email,
          tipo_consulta: tipo,
          asunto,
          mensaje,
        },
      ]);

      const body = new URLSearchParams();
      body.append("name", nombre);
      body.append("email", email);
      body.append("tipo_consulta", tipo);
      body.append("asunto", asunto);
      body.append("mensaje", mensaje);
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        formRef.current?.reset();
      } else {
        setError("Hubo un problema al enviar. Por favor intenta de nuevo.");
      }
    } catch {
      setError("Error de conexión. Verifica tu internet e intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "ContactPage",
            name: "Contacto Aviación Insight RD",
            description: "Página de contacto de Aviación Insight RD, portal de noticias aeronáuticas.",
            url: `${import.meta.env.VITE_SITE_URL || 'https://aviacioninsightrd.com'}/contacto`,
            mainEntity: {
              "@type": "Organization",
              name: "Aviación Insight RD",
              url: import.meta.env.VITE_SITE_URL || window.location.origin,
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0B1F3A] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20airport%20terminal%20interior%20wide%20angle%20shot%2C%20travelers%20silhouettes%2C%20warm%20lighting%2C%20glass%20ceiling%20architecture%2C%20aviation%20hub%2C%20professional%20photography%2C%20contemporary%20design&width=1920&height=600&seq=contacto-hero&orientation=landscape"
            alt="Contacto Aviación Insight RD"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A] via-[#0B1F3A]/90 to-[#0B1F3A]/70" />
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-blue-400" />
            <span className="text-blue-400 text-xs font-inter font-bold tracking-widest uppercase">
              Contacto
            </span>
          </div>
          <h1 className="text-white font-bold mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(2.2rem, 5vw, 3.5rem)" }}>
            CONTÁCTANOS
          </h1>
          <p className="text-blue-200/80 font-inter text-base max-w-xl">
            ¿Tienes una historia, propuesta o simplemente quieres conectar? Estamos aquí para escucharte. Lectores, medios y colaboradores son siempre bienvenidos.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#F8F9FB] py-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Left sidebar — info */}
            <div className="space-y-6">
              {/* Card info de contacto */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="text-[#0B1F3A] font-bold text-sm font-inter mb-4">Información de contacto</h3>
                <div className="space-y-3">
                  <a
                    href="mailto:info@aviacioninsightrd.com"
                    className="flex items-center gap-3 text-slate-600 hover:text-[#0B1F3A] transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-blue-600 text-sm" />
                    </div>
                    <span className="text-sm font-inter">info@aviacioninsightrd.com</span>
                  </a>
                </div>
              </div>

              {/* Card autora */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="mb-4">
                  <p className="text-[#0B1F3A] font-bold text-sm font-inter">Aviación Insight RD</p>
                  <p className="text-blue-600 text-xs font-inter">Noticias Aeronáuticas</p>
                  <p className="text-slate-400 text-xs font-inter">República Dominicana</p>
                </div>
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-slate-600 text-sm font-inter">
                    <i className="ri-time-line text-blue-400" />
                    Respuesta en 24–48 horas hábiles
                  </p>
                  <p className="flex items-center gap-2 text-slate-600 text-sm font-inter">
                    <i className="ri-shield-check-line text-blue-400" />
                    Tu información es confidencial
                  </p>
                </div>
                <p className="text-slate-600 font-inter text-sm leading-relaxed mt-4">
                  Respondemos personalmente a cada mensaje. Completa el formulario y nos pondremos en contacto contigo pronto.
                </p>
              </div>

              {/* Tipos de contacto */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="text-[#0B1F3A] font-bold text-sm font-inter mb-4">¿En qué puedo ayudarte?</h3>
                <div className="space-y-3">
                  {[
                    { icon: "ri-user-heart-line", label: "Lectores y seguidores", desc: "Preguntas, comentarios y sugerencias" },
                    { icon: "ri-newspaper-line", label: "Medios de comunicación", desc: "Solicitudes de entrevistas y colaboración periodística" },
                    { icon: "ri-shake-hands-line", label: "Colaboraciones", desc: "Propuestas de contenido y proyectos" },
                    { icon: "ri-megaphone-line", label: "Publicidad y patrocinio", desc: "Espacios publicitarios en el portal" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-blue-50 rounded-lg flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-blue-600 text-sm`} />
                      </div>
                      <div>
                        <p className="text-[#0B1F3A] font-semibold text-xs font-inter">{item.label}</p>
                        <p className="text-slate-400 text-xs font-inter leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Redes sociales */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="text-[#0B1F3A] font-bold text-sm font-inter mb-4">Sígueme en redes</h3>
                <div className="space-y-3">
                  {[
                    { icon: "ri-facebook-fill", label: "Facebook", handle: "Aviación Insight RD", href: "https://www.facebook.com/profile.php?id=61591088725425", color: "text-blue-600" },
                    { icon: "ri-instagram-line", label: "Instagram", handle: "@aviacioninsightrd", href: "https://www.instagram.com/aviacioninsightrd?utm_source=qr", color: "text-pink-500" },
                    { icon: "ri-twitter-x-line", label: "X", handle: "@aviacioninsight", href: "https://x.com/aviacioninsight?s=11&t=_ylQO8qtabGaqp-OhtGSBA", color: "text-slate-700" },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex items-center gap-3 hover:bg-slate-50 rounded-lg p-2 -mx-2 transition-colors cursor-pointer"
                    >
                      <div className={`w-8 h-8 flex items-center justify-center ${s.color}`}>
                        <i className={`${s.icon} text-lg`} />
                      </div>
                      <div>
                        <p className="text-[#0B1F3A] font-semibold text-xs font-inter">{s.label}</p>
                        <p className="text-slate-400 text-xs font-inter">{s.handle}</p>
                      </div>
                      <div className="ml-auto w-4 h-4 flex items-center justify-center text-slate-300">
                        <i className="ri-arrow-right-up-line text-xs" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="bg-[#0B1F3A] px-8 py-6">
                  <h2 className="text-white font-bold text-xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    ENVIAR MENSAJE
                  </h2>
                  <p className="text-blue-300 text-sm font-inter mt-1">Todos los campos son obligatorios</p>
                </div>

                {submitted ? (
                  <div className="px-8 py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                      <i className="ri-check-double-line text-green-600 text-2xl" />
                    </div>
                    <h3 className="text-[#0B1F3A] font-bold text-xl font-inter mb-2">¡Mensaje enviado con éxito!</h3>
                    <p className="text-slate-500 font-inter text-sm max-w-sm mx-auto">
                      Gracias por contactar a Aviación Insight RD. Recibirás una respuesta en un plazo de 24–48 horas hábiles.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 px-4 md:px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-inter font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Enviar otro mensaje
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="px-8 py-8 space-y-5"
                  >
                    {/* Nombre + Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#0B1F3A] font-inter font-semibold text-xs mb-2 uppercase tracking-wide">
                          Nombre completo
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={nombre}
                          onChange={(e) => setNombre(e.target.value)}
                          placeholder="Tu nombre"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-inter text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[#0B1F3A] font-inter font-semibold text-xs mb-2 uppercase tracking-wide">
                          Correo electrónico
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="tu@email.com"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-inter text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    {/* Tipo + Asunto */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[#0B1F3A] font-inter font-semibold text-xs mb-2 uppercase tracking-wide">
                          Tipo de consulta
                        </label>
                        <select
                          name="tipo_consulta"
                          value={tipo}
                          onChange={(e) => setTipo(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-inter text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer bg-white"
                        >
                          <option value="">Selecciona una opción</option>
                          {tiposConsulta.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#0B1F3A] font-inter font-semibold text-xs mb-2 uppercase tracking-wide">
                          Asunto
                        </label>
                        <input
                          type="text"
                          name="asunto"
                          value={asunto}
                          onChange={(e) => setAsunto(e.target.value)}
                          placeholder="¿De qué se trata tu mensaje?"
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-inter text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    {/* Mensaje */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-[#0B1F3A] font-inter font-semibold text-xs uppercase tracking-wide">
                          Mensaje
                        </label>
                        <span className={`text-xs font-inter ${mensaje.length > 450 ? "text-red-500" : "text-slate-400"}`}>
                          {mensaje.length}/500
                        </span>
                      </div>
                      <textarea
                        name="mensaje"
                        value={mensaje}
                        onChange={(e) => e.target.value.length <= 500 && setMensaje(e.target.value)}
                        placeholder="Escribe tu mensaje aquí..."
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-inter text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        <i className="ri-error-warning-line text-red-500 text-base" />
                        <p className="text-red-600 text-xs font-inter">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 bg-[#0B1F3A] hover:bg-[#0d2547] text-white font-inter font-semibold text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando mensaje...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          Enviar Mensaje
                        </>
                      )}
                    </button>

                    <p className="text-slate-400 text-xs font-inter text-center">
                      Tu información es confidencial y nunca será compartida con terceros.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </main>
  );
}
